/**
 * Question helpers for the Drag & Drop sorting stage (old/draganddrop.php).
 * Each question is a statement that belongs in exactly one of the theme's
 * configured answer categories (Rules & Settings). CSV-imported rows may only
 * fill a subset of option_one..option_six, but the game always shows every
 * configured category as a bucket — the row's own options are only used to
 * resolve which category is correct.
 */

import { ensureThemeJsonArray } from "../../functions/themeAssets";

const OPTION_KEYS = [
  "option_one",
  "option_two",
  "option_three",
  "option_four",
  "option_five",
  "option_six",
];

const MIN_BUCKETS = 2;
export const MAX_BUCKETS = 6;

export function normalizeCategoryName(name) {
  return String(name ?? "").trim().toLowerCase();
}

/** The theme's configured answer categories ({ name, color, image }) — the fixed
 *  bucket set every question drops into. */
export function getDragDropCategories(themeData) {
  return ensureThemeJsonArray(themeData?.categories);
}

/**
 * Normalize one quiz row into { id, question_name, correctAnswer, options } or
 * null if unusable. `correctAnswer` is resolved to the exact configured category
 * name whenever possible, so the game can show the full category set as buckets
 * regardless of how many option_* columns a CSV row happened to fill in.
 */
export function normalizeDragDropQuestion(q, categories = []) {
  if (!q || typeof q !== "object") return null;
  const question_name = String(q.question_name ?? q.question ?? "").trim();
  if (!question_name) return null;

  const correctRaw = String(q.correct_answer ?? "").trim();
  const categoryByName = new Map(
    (categories || []).map((c) => [normalizeCategoryName(c?.name), String(c?.name ?? "").trim()])
  );

  // Preferred path: correct_answer matches a configured category directly — no
  // option_* columns needed at all, since categories alone define the buckets now.
  let correctAnswer = categoryByName.get(normalizeCategoryName(correctRaw)) || "";
  let options = [];

  if (!correctAnswer) {
    // Legacy fallback for rows saved before questions moved to categories-only:
    // resolve against this row's own filled option_one..six (by text, then by
    // answer_id position), then map that label onto a configured category.
    const allOptions = OPTION_KEYS.map((key) => String(q[key] ?? "").trim());
    let filledCount = 0;
    for (const opt of allOptions) {
      if (!opt) break;
      filledCount += 1;
    }

    const rawShow = parseInt(String(q.showOption ?? "").trim(), 10);
    const count =
      Number.isFinite(rawShow) && rawShow >= MIN_BUCKETS
        ? Math.min(MAX_BUCKETS, rawShow)
        : filledCount;
    const optionCount = Math.min(MAX_BUCKETS, count || filledCount);

    if (optionCount >= MIN_BUCKETS) {
      const shapeOptions = allOptions.slice(0, optionCount);
      if (!shapeOptions.some((opt) => !opt)) {
        options = shapeOptions;
        let label = options.find(
          (opt) => normalizeCategoryName(opt) === normalizeCategoryName(correctRaw)
        );
        if (!label) {
          const aid = parseInt(String(q.answer_id ?? "").trim(), 10);
          if (Number.isFinite(aid) && aid >= 1 && aid <= options.length) {
            label = options[aid - 1];
          }
        }
        if (label) {
          correctAnswer = categoryByName.get(normalizeCategoryName(label)) || label;
        }
      }
    }
  }

  if (!correctAnswer) return null;

  return {
    id: q.id ?? `${question_name}-${normalizeCategoryName(correctAnswer)}`,
    question_name,
    correctAnswer,
    options,
  };
}

export function isDragDropQuestionPlayable(q, categories) {
  return normalizeDragDropQuestion(q, categories) !== null;
}

/** Theme rows may hold the quiz flat, or as `questions`. */
function readQuizList(themeData) {
  const direct = ensureThemeJsonArray(themeData?.quiz);
  if (direct.length) return direct;
  return ensureThemeJsonArray(themeData?.questions);
}

/** All playable drag & drop statements configured on the theme. */
export function getDragDropQuestions(themeData) {
  const categories = getDragDropCategories(themeData);
  return readQuizList(themeData)
    .map((q) => normalizeDragDropQuestion(q, categories))
    .filter(Boolean);
}

export function shuffleCopy(arr) {
  const next = arr.slice();
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const t = next[i];
    next[i] = next[j];
    next[j] = t;
  }
  return next;
}

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
